import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  ArrowLeft, 
  Trophy, 
  Target, 
  Users, 
  Calendar, 
  Award,
  Star,
  TrendingUp,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ChallengeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isParticipating, setIsParticipating] = useState(false);

  // Mock challenge data
  const challenge = {
    id: parseInt(id),
    name: 'November Referral Sprint',
    description: 'Complete 50 referrals this month to earn bonus points and exclusive rewards. This challenge runs through the end of November and is open to all referring doctors.',
    target: 50,
    current: 34,
    participants: 28,
    reward: 500,
    status: 'active',
    endsIn: '12 days',
    startDate: '2025-11-01',
    endDate: '2025-11-30',
    icon: '🏃',
    type: 'referral',
    difficulty: 'medium',
    rules: [
      'All referrals must be completed within the challenge period',
      'Only verified referrals count towards the total',
      'Participants must maintain quality standards',
      'Points awarded upon challenge completion'
    ],
    milestones: [
      { count: 10, reward: 50, reached: true },
      { count: 25, reward: 100, reached: true },
      { count: 40, reward: 150, reached: false },
      { count: 50, reward: 200, reached: false }
    ]
  };

  const leaderboard = [
    { rank: 1, name: 'Dr. Sarah Johnson', progress: 47, points: 450, avatar: 'SJ' },
    { rank: 2, name: 'Dr. Michael Chen', progress: 43, points: 400, avatar: 'MC' },
    { rank: 3, name: 'Dr. Gerard Nikodem', progress: 38, points: 350, avatar: 'GN' },
    { rank: 4, name: 'Dr. David Schertzer', progress: 34, points: 300, avatar: 'DS' },
    { rank: 5, name: 'Dr. Elizabeth Fassler', progress: 32, points: 280, avatar: 'EF' }
  ];

  const handleJoinChallenge = () => {
    setIsParticipating(true);
    alert('Successfully joined the challenge!');
  };

  const handleLeaveChallenge = () => {
    if (window.confirm('Are you sure you want to leave this challenge? Your progress will be saved.')) {
      setIsParticipating(false);
      alert('You have left the challenge.');
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Button
        variant="ghost"
        onClick={() => navigate('/challenges')}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Challenges
      </Button>

      {/* Challenge Header */}
      <Card className="mb-6 border-l-4 border-[#D4AF37]">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1">
              <div className="text-6xl">{challenge.icon}</div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <CardTitle className="text-3xl">{challenge.name}</CardTitle>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    {challenge.status}
                  </Badge>
                  <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                    {challenge.difficulty}
                  </Badge>
                </div>
                <p className="text-gray-600 mb-4">{challenge.description}</p>
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <span className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {challenge.startDate} - {challenge.endDate}
                  </span>
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Ends in {challenge.endsIn}
                  </span>
                  <span className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {challenge.participants} participants
                  </span>
                  <span className="flex items-center">
                    <Star className="h-4 w-4 mr-1 text-[#D4AF37]" />
                    {challenge.reward} points reward
                  </span>
                </div>
              </div>
            </div>
            <div>
              {isParticipating ? (
                <Button
                  variant="outline"
                  onClick={handleLeaveChallenge}
                  className="border-red-500 text-red-500 hover:bg-red-50"
                >
                  Leave Challenge
                </Button>
              ) : (
                <Button
                  className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                  onClick={handleJoinChallenge}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Join Challenge
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle>Your Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Completion</span>
                  <span className="font-semibold text-[#0A1628]">
                    {challenge.current} / {challenge.target}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] h-4 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min((challenge.current / challenge.target) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {Math.round((challenge.current / challenge.target) * 100)}% complete - 
                  {challenge.target - challenge.current} more to go!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Milestones */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Target className="h-5 w-5 mr-2 text-[#D4AF37]" />
                Milestones & Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {challenge.milestones.map((milestone, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                      milestone.reached 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {milestone.reached ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : (
                        <div className="h-6 w-6 rounded-full border-2 border-gray-300" />
                      )}
                      <div>
                        <p className="font-semibold text-[#0A1628]">{milestone.count} Referrals</p>
                        <p className="text-sm text-gray-600">+{milestone.reward} bonus points</p>
                      </div>
                    </div>
                    {milestone.reached && (
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        Claimed
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Rules */}
          <Card>
            <CardHeader>
              <CardTitle>Challenge Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {challenge.rules.map((rule, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#D4AF37] mr-2">•</span>
                    <span className="text-gray-700">{rule}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Leaderboard */}
          <Card className="bg-gradient-to-br from-[#0A1628] to-[#1a2332] text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-[#D4AF37]">
                <Trophy className="h-6 w-6 mr-2" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.map((entry) => (
                  <div 
                    key={entry.rank}
                    className={`p-3 rounded-lg ${
                      entry.rank <= 3 
                        ? 'bg-white/10 border-2 border-[#D4AF37]' 
                        : 'bg-white/5'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-xl font-bold">
                        {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                      </div>
                      <Avatar className="h-10 w-10 border-2 border-[#D4AF37]">
                        <AvatarFallback className="bg-[#D4AF37] text-[#0A1628] font-bold">
                          {entry.avatar}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{entry.name}</p>
                        <p className="text-xs text-[#D4AF37]">{entry.progress} / {challenge.target}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{entry.points}</p>
                        <p className="text-xs text-gray-400">pts</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Rank</span>
                <span className="font-semibold text-[#0A1628]">#4</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Points Earned</span>
                <span className="font-semibold text-[#D4AF37]">300</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Days Remaining</span>
                <span className="font-semibold text-blue-600">{challenge.endsIn}</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm text-gray-600">Avg per Day</span>
                <span className="font-semibold text-green-600">2.8</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChallengeDetail;
